import { useQuery } from "@tanstack/react-query"
import { format, subDays } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { ArrowUpRight } from "lucide-react"
import { useMemo } from "react"
import { Link } from "react-router-dom"
import { getSummaryStats, statsKeys } from "@/entities/bot-stats"
import { LineChart, LineChartSkeleton } from "@/shared/ui/charts/line-chart"
import { Skeleton } from "@/shared/ui/skeleton"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { botPath, useBotWorkspace, WorkspaceSection } from "@/widgets/bot-workspace"
import { LoadSwap } from "@/shared/ui/load-swap"

const DAYS = 30

/** Плитка с числом; со ссылкой открывает этих людей в «Пользователях». */
function Tile({
	label,
	value,
	hint,
	to,
}: {
	label: string
	value: number | string
	hint?: string
	to?: string
}) {
	const body = (
		<>
			<p className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
				{label}
				{to ? (
					<ArrowUpRight className="size-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
				) : null}
			</p>
			<p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
			{hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
		</>
	)
	const box = "rounded-2xl border border-border bg-card p-4"
	if (!to) return <div className={box}>{body}</div>
	return (
		<Link
			to={to}
			title="Открыть этих людей"
			className={`${box} group transition-colors hover:border-primary/40 hover:bg-secondary/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none`}
		>
			{body}
		</Link>
	)
}

/** Заглушка сводки: две строки плиток и график, на своих местах. */
function SummarySkeleton() {
	return (
		<div aria-hidden="true" className="space-y-6">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				{Array.from({ length: 8 }, (_, index) => (
					<div key={index} className="rounded-2xl border border-border bg-card p-4">
						<div className="flex h-4 items-center">
							<Skeleton className="h-3 w-28 rounded" />
						</div>
						<div className="mt-1 flex h-8 items-center">
							<Skeleton className="h-6 w-16 rounded-lg" />
						</div>
						{index === 0 || index >= 6 ? (
							<div className="mt-1 flex h-4 items-center">
								<Skeleton className="h-3 w-32 rounded" />
							</div>
						) : null}
					</div>
				))}
			</div>
			<div className="rounded-2xl border border-border bg-card p-5">
				<div className="mb-4">
					<div className="flex h-5 items-center">
						<Skeleton className="h-3.5 w-36 rounded" />
					</div>
					<div className="mt-0.5 flex h-4 items-center">
						<Skeleton className="h-3 w-48 rounded" />
					</div>
				</div>
				<LineChartSkeleton series={1} />
			</div>
		</div>
	)
}

const since = (days: number) => encodeURIComponent(subDays(new Date(), days).toISOString())

/** «Аналитика → Сводка»: сколько людей в боте, насколько активны, сколько новых. */
export function SummaryPage() {
	const { bot } = useBotWorkspace()
	const stats = useQuery({
		queryKey: statsKeys.summary(bot.id),
		queryFn: () => getSummaryStats(bot.id),
		refetchInterval: 60_000,
	})

	return (
		<WorkspaceSection title="Сводка" subtitle="Общая аналитика по боту">
			<LoadSwap loading={stats.isLoading} id={stats.data ? "ready" : "error"}>
				{stats.isLoading ? (
					<SummarySkeleton />
				) : stats.isError || !stats.data ? (
					<LoadErrorState onRetry={() => void stats.refetch()} />
				) : (
					<SummaryContent data={stats.data} users={botPath(bot.id, "analytics/users")} />
				)}
			</LoadSwap>
		</WorkspaceSection>
	)
}

function SummaryContent({
	data,
	users,
}: {
	data: NonNullable<Awaited<ReturnType<typeof getSummaryStats>>>
	/** Адрес «Пользователей»: плитки ведут туда с готовым фильтром. */
	users: string
}) {
	const { sessions, pending, newByDay } = data
	// Сервер отдаёт только дни с новыми людьми: пустые дни дополняем нулями, иначе ось врёт.
	const series = useMemo(() => {
		const byDay = new Map(newByDay.map((row) => [row.day.slice(0, 10), row.count]))
		const today = new Date()
		return Array.from({ length: DAYS }, (_, index) => {
			const date = format(subDays(today, DAYS - 1 - index), "yyyy-MM-dd")
			return { date, count: byDay.get(date) ?? 0 }
		})
	}, [newByDay])
	return (
		<div className="space-y-6">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<Tile
					label="Всего пользователей"
					value={sessions.total + pending.total}
					hint={pending.total > 0 ? `из них без Telegram ID: ${pending.total}` : undefined}
					to={users}
				/>
				<Tile
					label="Активны за сутки"
					value={sessions.activeDay}
					to={`${users}?activeAfter=${since(1)}`}
				/>
				<Tile
					label="Активны за 7 дней"
					value={sessions.activeWeek}
					to={`${users}?activeAfter=${since(7)}`}
				/>
				<Tile
					label="Активны за 30 дней"
					value={sessions.activeMonth}
					to={`${users}?activeAfter=${since(30)}`}
				/>
				<Tile
					label="Новых за 7 дней"
					value={sessions.newWeek}
					to={`${users}?joinedAfter=${since(7)}`}
				/>
				<Tile label="Заблокировали бота" value={sessions.blocked} to={`${users}?state=blocked`} />
				<Tile label="Пишут боту" value={sessions.total} hint="с Telegram ID" to={users} />
				<Tile
					label="Не найти Telegram ID"
					value={pending.notFound}
					hint="нет username: придут сами, когда напишут боту"
				/>
			</div>

			<section className="rounded-2xl border border-border bg-card p-5">
				<header className="mb-4">
					<h3 className="text-sm font-semibold">Новые пользователи</h3>
					<p className="mt-0.5 text-xs text-muted-foreground">По дням за последние 30 дней</p>
				</header>
				<LineChart
					data={series}
					series={[{ key: "count", label: "Новые пользователи" }]}
					formatDate={(date) => format(new Date(`${date}T00:00:00`), "d MMM", { locale: ru })}
					ariaLabel="Новые пользователи бота по дням за 30 дней"
				/>
			</section>
		</div>
	)
}
